// Host-based routing for the single Netlify site served under several domain
// aliases of ONE build:
//   akara.ae / www.akara.ae → marketing + guest deposit pages
//   app.akara.ae            → merchant dashboard + admin + auth
//   docs.akara.ae           → documentation (its own chrome, see App.jsx)
//
// resolveHostRedirect() returns an absolute URL to send the browser to, or null
// to render the current path in place. Only the real production hosts trigger
// redirects — on localhost and Netlify deploy previews every path renders in
// place, so the whole app stays testable on a single origin.
//
// ponytail: hosts hardcoded, matching the existing docs.akara.ae handling. The
// domain is stable; an env-var indirection would be config for a value that
// never changes.

const APEX = 'akara.ae'
const WWW = 'www.akara.ae'
const APP = 'app.akara.ae'

export const APEX_ORIGIN = `https://${APEX}`
export const APP_ORIGIN = `https://${APP}`
export const DOCS_ORIGIN = 'https://docs.akara.ae'

// Paths that belong on app.akara.ae. Everything else (marketing, /deposit guest
// links, /faq, /pricing, …) belongs on the apex.
function isAppPath(p) {
  return (
    p === '/dashboard' || p.startsWith('/dashboard/') ||
    p === '/admin' || p.startsWith('/admin/') ||
    p === '/login' ||
    p === '/get-started' ||
    p === '/reset-password'
  )
}

function isDocsPath(p) {
  return (
    p === '/docs' || p.startsWith('/docs/') ||
    p === '/documentation' || p.startsWith('/documentation/')
  )
}

export function resolveHostRedirect(host, pathname, search = '', hash = '') {
  const dest = (origin) => origin + pathname + search + hash
  // Prerendered static routes are served with a trailing slash (/get-started/);
  // match against the canonical slash-less form so host routing still applies
  // (otherwise /get-started/ on the apex never bounces to the app subdomain).
  const p =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

  if (host === APEX || host === WWW) {
    if (isDocsPath(p)) return dest(DOCS_ORIGIN)
    if (isAppPath(p)) return dest(APP_ORIGIN)
    return null
  }

  if (host === APP) {
    // Bare app root → the dashboard; ProtectedRoute bounces to /login if needed.
    if (pathname === '/') return `${APP_ORIGIN}/dashboard${search}${hash}`
    // Non-app paths reached on the app host (marketing, guest /deposit, docs)
    // go back to the apex, which applies any further hop (e.g. docs → docs host).
    if (!isAppPath(p)) return dest(APEX_ORIGIN)
    return null
  }

  // docs.akara.ae, localhost, deploy previews: render in place.
  return null
}

// Absolute URL for an app-subdomain path (e.g. /login) when the user is browsing
// the public apex, so the link opens app.akara.ae directly — used with
// target="_blank" to open auth in a new tab. On localhost, deploy previews, or
// the app host itself, returns the plain path so the link still resolves in place.
export function appHref(path) {
  if (typeof window === 'undefined') return path
  const host = window.location.hostname
  if (host === APEX || host === WWW) return APP_ORIGIN + path
  return path
}

// Canonical origin for guest-facing deposit links. Guests live on the apex, and
// a link generated from the dashboard (app.akara.ae) must match the one emailed
// by the Edge Functions (SITE_URL → akara.ae). In dev/preview, use the current
// origin so a copied link still resolves locally.
export function guestLinkOrigin() {
  if (typeof window === 'undefined') return APEX_ORIGIN
  const host = window.location.hostname
  if (host === APEX || host === WWW || host === APP) return APEX_ORIGIN
  return window.location.origin
}
